import { createRef } from 'react';
import { createPortal } from 'react-dom';
import JxLineEdit from './JxLineEdit';
import '../JxSelect/JxSelect.css';
import './JxComboEdit.css';

/**
 * JxComboEdit — JxLineEdit with free text plus a ▼ suggestion list.
 * Picking a suggestion sets its value, typing anything else is allowed too.
 * Dropdown via createPortal on document.body, reuses the JxSelect paper styling.
 *
 * @param {Array<{label:string,value:string}>} options - suggestions, ▼ hidden when empty
 * (other props: see JxLineEdit)
 */
export default class JxComboEdit extends JxLineEdit {
    static defaultProps = { options: [] };

    constructor(props) {
        super(props);
        this.state = { open: false, dropdownStyle: {}, highlighted: -1 };
        this._rootRef = createRef();
        this._paperRef = createRef();
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this._onOutside, true);
        super.componentWillUnmount?.();
    }

    getPropsToOmit() {
        return ['options'];
    }

    getSuffix() {
        if (this.props.options.length === 0) return null;
        return (
            <span className="jx-combo-edit-toggle" onMouseDown={this._onToggle}>
                {this.state.open ? '▲' : '▼'}
            </span>
        );
    }

    handleKeyDown(e) {
        if (e.key === 'ArrowDown') {
            this._move(e, 1);
            return;
        }
        if (e.key === 'ArrowUp') {
            this._move(e, -1);
            return;
        }
        if (e.key === 'Enter' && this._selectHighlighted(e)) return;
        if (e.key === 'Escape' && this.state.open) {
            e.preventDefault();
            e.stopPropagation(); // close the list, not the dialog
            this._close();
            return;
        }
        super.handleKeyDown(e);
    }

    /**
     * Moves the highlight, opens the list first when closed.
     * @param {KeyboardEvent} e
     * @param {number} dir - 1 down, -1 up
     */
    _move(e, dir) {
        if (this.props.options.length === 0) return;
        e.preventDefault();
        if (!this.state.open) {
            this._open();
            return;
        }

        const last = this.props.options.length - 1;
        const next = Math.min(last, Math.max(0, this.state.highlighted + dir));
        this.setState({ highlighted: next });
    }

    /**
     * Enter picks the highlighted option, otherwise Enter keeps its normal onEnter.
     * @param {KeyboardEvent} e
     * @returns {boolean} true when an option was picked
     */
    _selectHighlighted(e) {
        if (!this.state.open) return false;
        const option = this.props.options[this.state.highlighted];
        if (!option) return false;

        e.preventDefault();
        this._select(option);
        return true;
    }

    _onToggle = (e) => {
        e.preventDefault(); // keep focus in the input
        if (this.state.open) {
            this._close();
            return;
        }
        this._open();
    };

    // Auto open on enter (tab or click), never auto select
    _onFocus = () => {
        if (this.state.open || this.props.options.length === 0) return;
        this._open();
    };

    // Option/toggle mousedown keeps focus, so this only fires when leaving the field
    _onBlur = () => {
        if (!this.state.open) return;
        this._close();
    };

    _open() {
        const rect = this._rootRef.current.getBoundingClientRect();
        const dropdownStyle = { position: 'fixed', top: rect.bottom + 4, left: rect.left, width: rect.width, zIndex: 20001 };
        this.setState({ open: true, dropdownStyle, highlighted: -1 });
        document.addEventListener('mousedown', this._onOutside, true);
    }

    _close() {
        this.setState({ open: false });
        document.removeEventListener('mousedown', this._onOutside, true);
    }

    // Capture phase — a dialog's stopPropagation must not swallow it
    _onOutside = (e) => {
        if (this._rootRef.current?.contains(e.target)) return;
        if (this._paperRef.current?.contains(e.target)) return;
        this._close();
    };

    _select(option) {
        this.props.onChange(option.value);
        this._close();
    }

    _renderOption = (o, i) => {
        const selected = o.value === this.props.value ? 'jx-select-option--selected' : '';
        const focused = i === this.state.highlighted ? 'Mui-focused' : '';
        return (
            <div
                key={o.value}
                className={`MuiAutocomplete-option ${selected} ${focused}`}
                onMouseEnter={() => this.setState({ highlighted: i })}
                onMouseDown={(e) => {
                    e.preventDefault();
                    this._select(o);
                }}
            >
                {o.label}
            </div>
        );
    };

    _renderDropdown() {
        if (!this.state.open) return null;
        return createPortal(
            <div ref={this._paperRef} className="jx-select-paper jx-select-dropdown" style={this.state.dropdownStyle}>
                <div className="MuiAutocomplete-listbox">{this.props.options.map(this._renderOption)}</div>
            </div>,
            document.body,
        );
    }

    render() {
        return (
            <div ref={this._rootRef} className="jx-combo-edit" onFocus={this._onFocus} onBlur={this._onBlur}>
                {super.render()}
                {this._renderDropdown()}
            </div>
        );
    }
}
